import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import dayjs, { OpUnitType } from 'dayjs/esm/index';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import isBetween from 'dayjs/esm/plugin/isBetween';
import isoWeek from 'dayjs/esm/plugin/isoWeek';
import isSameOrBefore from 'dayjs/esm/plugin/isSameOrBefore';
import utc from 'dayjs/esm/plugin/utc';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs().format();

const FIRST_DAY_KEY = 'FLIPTO_FIRST_DAY_OF_WEEK';

export type AppDate = string | number | Date | dayjs.Dayjs;

export class DateUtils {
    static toString(date: AppDate, format = 'YYYY-MM-DD'): string {
        return date ? dayjs(date).format(format) : null;
    }


    static ngbDateToDate(ngbDate: NgbDate): Date {
        return new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
    }

    static dateToNgbDate(date: Date): NgbDate {
        return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }

    static toDateFormat(date: Date | NgbDate): Date {
        return date instanceof Date ? date : DateUtils.ngbDateToDate(date);
    }

    static isSame(date1: AppDate, date2: AppDate, unit: any = 'day'): boolean {
        if (date1 && date2) {
            return dayjs(date1).isSame(date2, unit);
        }
        return null;
    }

    static isBetween(start: AppDate, end: AppDate, date: AppDate, unit: OpUnitType = 'day', d: any = '[]'): boolean {
        if (start && end && date) {
            return dayjs(date)?.isBetween(start, end, unit, d);
        }
        return null;
    }

    static isBefore(date: AppDate, beforeDate: AppDate, inclusive = false): boolean {
        return dayjs(date).isBefore(beforeDate, 'day') || (inclusive && dayjs(date).isSame(beforeDate, 'day'));
    }

    static isAfter(date: AppDate, afterDate: AppDate, inclusive = false): boolean {
        return dayjs(date).isAfter(afterDate, 'day') || (inclusive && dayjs(date).isSame(afterDate, 'day'));
    }

    static year(date?: AppDate) {
        return dayjs(date || new Date()).year();
    }

    static duration(startDate: AppDate, endDate: AppDate, unit: OpUnitType = 'days', format?: string): number {
        if (format) {
            endDate = dayjs(endDate, format);
            startDate = dayjs(startDate, format);
        } else {
            endDate = dayjs(endDate);
        }
        return endDate.diff(startDate, unit);
    }

    static subtract(date: Date | string, amount: number, unit: dayjs.ManipulateType = 'day'): dayjs.Dayjs {
        return dayjs(date).subtract(amount, unit);
    }

    static add(date: Date | string, amount: number, unit: dayjs.ManipulateType = 'day'): dayjs.Dayjs {
        return dayjs(date).add(amount, unit);
    }

    static startOfDay(date: AppDate): Date {
        return dayjs(date).startOf('day').utc(true).toDate();
    }

    static startOfMonth(date: AppDate): Date {
        return dayjs(date).startOf('month').toDate();
    }

    static endOfMonth(date: AppDate): Date {
        return dayjs(date).endOf('month').toDate();
    }

    static parse(date: AppDate, format: string): dayjs.Dayjs {
        return dayjs(date, format);
    }

    static getMonthsInRange(from: Date | string, to: Date | string, format: string = 'YYYY-MM'): string[] {
        const months: string[] = [];
        let current = dayjs(from).startOf('month');

        while (current.isSameOrBefore(dayjs(to), 'month')) {
            months.push(current.format(format));
            current = current.add(1, 'month');
        }

        return months;
    }

    static getFirstWeekdayInMonth(month: string, weekday: number): dayjs.Dayjs {
        let date = dayjs(month).startOf('month');
        while (date.day() !== weekday) {
            date = date.add(1, 'day');
        }
        return date;
    }

    static getLastWeekdayInMonth(month: string, weekday: number): dayjs.Dayjs {
        let date = dayjs(month).endOf('month');
        while (date.day() !== weekday) {
            date = date.subtract(1, 'day');
        }
        return date;
    }

    static isEndOfMonth(date: Date | string): boolean {
        return dayjs(date).isSame(dayjs(date).endOf('month'), 'day');
    }

    static monthKey(dateStr: string): string {
        // Fast path: dates from API/aggregators are canonical 'YYYY-MM-DD' strings.
        // Fall back to dayjs only if the input doesn't match that shape.
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            return dateStr.substring(0, 7);
        }
        const date = dayjs(dateStr);
        return date.format('YYYY-MM');
    }

    static weekKey(dateStr: string): string {
        const date = dayjs(dateStr);
        const firstDay = DateUtils.firstDay;
        // weekday: 0..6 (Sun..Sat)
        const weekday = date.day();
        // number of days to subtract to reach start of week
        const diff = (weekday - firstDay + 7) % 7;
        // canonical week start (Sunday or Monday depending on firstDay)
        const weekStart = date.subtract(diff, 'day');

        // To get ISO week number for this bucket:
        // - if weekStart is Monday (firstDay === 1): iso of weekStart is correct
        // - if weekStart is Sunday (firstDay === 0): use Monday = weekStart.add(1, 'day')
        const isoRef = firstDay === 1 ? weekStart : weekStart.add(1, 'day');

        const isoWeek = isoRef.isoWeek();
        const isoYear = isoRef.isoWeekYear();
        return `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
    }

    static weekdayKey(dateStr: string): string {
        const date = dayjs(dateStr);
        const isoDay = date.isoWeekday(); // 1 = Monday ... 7 = Sunday
        const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
        return days[isoDay - 1];
    }

    static weekRange(weekKey: string): { startDate: string; endDate: string; } {
        const match = weekKey.match(/^(\d{4})-W(\d{1,2})$/);
        if (!match) return null;

        const [, yearStr, weekStr] = match;
        const year = parseInt(yearStr, 10);
        const week = parseInt(weekStr, 10);

        // Create a reference date for that ISO week/year manually
        // Start with the first ISO week of that year (Monday)
        const isoWeek1Monday = dayjs(`${year}-01-04`).startOf('isoWeek'); // Monday of ISO week 1
        const isoWeekStart = isoWeek1Monday.add(week - 1, 'week');        // this week's Monday
        let startDate: dayjs.Dayjs;
        let endDate: dayjs.Dayjs;

        if (DateUtils.firstDay === 1) {
            // Show Monday - Sunday (ISO)
            startDate = isoWeekStart;
            endDate = isoWeekStart.add(6, 'day'); // Sunday
        } else {
            // Show Sunday - Saturday for the same ISO week number:
            // Shift the window one day earlier: Sunday = Monday - 1
            startDate = isoWeekStart.subtract(1, 'day'); // Sunday
            endDate = startDate.add(6, 'day');           // Saturday
        }

        return { startDate: startDate.format('YYYY-MM-DD'), endDate: endDate.format('YYYY-MM-DD') };
    }

    static diffWeeks(weekKey1: string, weekKey2: string): number {
        const match1 = weekKey1.match(/^(\d{4})-W(\d{1,2})$/);
        const match2 = weekKey2.match(/^(\d{4})-W(\d{1,2})$/);
        if (!match1 || !match2) return null;

        const [, yearStr1, weekStr1] = match1;
        const [, yearStr2, weekStr2] = match2;
        const year1 = parseInt(yearStr1, 10);
        const year2 = parseInt(yearStr2, 10);
        const week1 = parseInt(weekStr1, 10);
        const week2 = parseInt(weekStr2, 10);

        const start = dayjs().year(Number(year1)).isoWeek(week1).startOf('isoWeek');
        const end = dayjs().year(Number(year2)).isoWeek(week2).startOf('isoWeek');

        return end.diff(start, "week");
    }

    static formatDateRange(
        start: string | Date,
        end?: string | Date,
        formats?: { month?: string; day?: string; to?: string },
    ): string {
        const dayFormat = formats?.day || 'D';
        const monthFormat = formats?.month || 'MMM';
        const to = formats?.to || 'to';
        const s = dayjs(start);
        const e = end ? dayjs(end) : null;

        if (!e || s.isSame(e, 'day')) {
            return s.format(`${monthFormat} ${dayFormat}, YYYY`);
        }

        // Same month & year
        if (s.isSame(e, 'month') && s.isSame(e, 'year')) {
            return `${s.format(`${monthFormat} ${dayFormat}`)}–${e.format(`${dayFormat}, YYYY`)}`;
        }

        // Same year, different month
        if (s.isSame(e, 'year')) {
            return `${s.format(`${monthFormat} ${dayFormat}`)} ${to} ${e.format(`${monthFormat} ${dayFormat}, YYYY`)}`;
        }

        // Different years
        return `${s.format(`${monthFormat} ${dayFormat}, YYYY`)} ${to} ${e.format(`${monthFormat} ${dayFormat}, YYYY`)}`;
    }

    static get firstDay(): number {
        return window[FIRST_DAY_KEY] || 0;
    }

    static set firstDay(value: number) {
        window[FIRST_DAY_KEY] = value;
    }

    static greatest(date1: string, date2: string): string;
    static greatest(date1: number, date2: number): number;
    static greatest(date1: Date, date2: Date): Date;
    static greatest(date1: dayjs.Dayjs, date2: dayjs.Dayjs): dayjs.Dayjs;
    static greatest(date1: AppDate, date2: AppDate): AppDate;
    static greatest(date1: AppDate, date2: AppDate): AppDate {
        if (!date1) return date2;
        if (!date2) return date1;
        return DateUtils.isAfter(date1, date2) ? date1 : date2;
    }

    static least(date1: string, date2: string): string;
    static least(date1: number, date2: number): number;
    static least(date1: Date, date2: Date): Date;
    static least(date1: dayjs.Dayjs, date2: dayjs.Dayjs): dayjs.Dayjs;
    static least(date1: AppDate, date2: AppDate): AppDate;
    static least(date1: AppDate, date2: AppDate): AppDate {
        if (!date1) return date2;
        if (!date2) return date1;
        return DateUtils.isBefore(date1, date2) ? date1 : date2;
    }
}
